import React, { useState, useContext } from 'react';
import {
  Box, Button, Typography, Grid, Paper, Divider, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress,
  TextField, Tooltip,
} from '@mui/material';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useNavigate, Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useMutation, useQuery } from '@apollo/client';
import { UserContext } from '../../components/auth/auth-context';
import { useSelectedBusiness } from '../../redux/store';
import { BUY_COINS_MUTATION } from '../../graphql/mutations/tip';
import {
  GET_WALLET_BALANCE,
  GET_WALLET_SUMMARY,
  GET_COIN_TRANSACTIONS, 
} from '../../graphql/queries/tip';
import { useCoinConfig } from './tipProvider'; 
import { SEND_EMAIL_MUTATION } from '../../graphql/mutations';

/* ───────────────────────── Configuration ───────────────────────── */
const MIN_COINS = 5;               // <─── change this to tweak global minimum

/* ─────────── Helper: greedy pricing for any quantity ─────────── */
function calculateCustomPrice(quantity, packs, COIN_PRICE) {
  let remaining = quantity;
  let total = 0;
  const sorted = [...packs].sort((a, b) => b.coins - a.coins);

  for (const pack of sorted) {
    if (remaining >= pack.coins) {
      const count = Math.floor(remaining / pack.coins);
      total += count * pack.price;
      remaining -= count * pack.coins;
    }
  }
  total += remaining * COIN_PRICE;
  return Number(total.toFixed(2));
}

export const BuyCoinsPage = () => {
  const { providerUser } = useContext(UserContext);
  const selectedBusiness  = useSelectedBusiness();
  const navigate          = useNavigate();

  /* ───── GraphQL ───── */
   const [buyCoins, { loading: buying }]    = useMutation(BUY_COINS_MUTATION, {
    refetchQueries: [
      { query: GET_WALLET_BALANCE,     variables: { staffId: providerUser?.staffId } },
      { query: GET_WALLET_SUMMARY,     variables: { staffId: providerUser?.staffId } },
      { query: GET_COIN_TRANSACTIONS,  variables: { staffId: providerUser?.staffId } },
    ],
    awaitRefetchQueries: true,
  });
  const { data: walletData, loading: walletLoading } = useQuery(
    GET_WALLET_BALANCE,
    { variables: { staffId: providerUser?.staffId }, skip: !providerUser?.staffId },
  );

  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  /* ───── Local state ───── */
  const [selectedPack, setSelectedPack] = useState(null);
  const [paypalOpen,  setPaypalOpen]    = useState(false);
  const [successOpen, setSuccessOpen]   = useState(false);
  const [customCoins, setCustomCoins]   = useState('');
  const [ppLoading,   setPpLoading]     = useState(false);



  /* ───── Config & derived values ───── */
  const { purchaseRate: COIN_PRICE, packs: coinPacks } = useCoinConfig();
  const currentBalance = walletData?.getPlanyCoinWallet?.balance ?? 0;

  const biz        = selectedBusiness?.businessBasic?.url;
  const staff      = providerUser?.url;
  const walletPath = biz && staff ? `/${biz}/${staff}/wallet` : '/login';

  const coinQty       = Number(customCoins);
  const customPrice   = calculateCustomPrice(coinQty, coinPacks, COIN_PRICE);
  const customSavings = Number((coinQty * COIN_PRICE - customPrice).toFixed(2));
  const customInvalid = isNaN(coinQty) || coinQty < MIN_COINS;
  const buyerEmail = providerUser.email;


  /* ───── Handlers ───── */
  const handleBuyClick     = (pack) => { setSelectedPack(pack); setPaypalOpen(true); };
  const handlePaypalClose  = () => { if (!ppLoading) { setPaypalOpen(false); setSelectedPack(null); } };
  const handleSuccessClose = () => {
    setSuccessOpen(false);
    setSelectedPack(null);
    navigate(walletPath, { state: { didPurchase: true } });
  };

  const onApprove = async (_data, actions) => {
    setPpLoading(true);
    const captureDetails = await actions.order.capture();
    try {
      await buyCoins({
        variables: {
          input: {
            amount:    selectedPack.coins,
            packCoins: selectedPack.coins,
            orderId:   captureDetails.id,
          },
        },
      });
      const buyerEmail = providerUser.email;
      const coinAmt = selectedPack.coins;
      const usdPaid = selectedPack.price.toFixed(2);

      sendEmail({
        variables: {
          from_email: 'no-reply@plany365.com',
          to: buyerEmail,
          subject: `PlanyCoin purchase receipt – ${coinAmt} coins`,
          text: `Thank you for purchasing ${coinAmt} PlanyCoins for $${usdPaid}.`,
          html: `<p>Thank you for purchasing <strong>${coinAmt} PlanyCoins</strong> for $${usdPaid}.</p><p>You can view them in your wallet now.</p>`,
        },
      }).catch(console.error);

      setPaypalOpen(false);
      setSuccessOpen(true);
    } catch (err) {
      console.error(err);
      alert('There was an error finalizing your purchase.');
    } finally {
      setPpLoading(false);
    }
  };

  /* ───── Render ───── */
  return (
    <Box p={3}>
      {/* Back link */}
      <Button component={Link} to={walletPath} startIcon={<ArrowBackIosIcon />} sx={{ mb: 2 }}>
        Back to Wallet
      </Button>

      <Typography variant="h5" gutterBottom>🛒 Buy PlanyCoins</Typography>

      {walletLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          💰 Current Balance: {currentBalance} PlanyCoins
        </Typography>
      )}

      <Typography variant="body1" sx={{ mb: 2 }}>
        Select a pack, or enter a custom amount (min {MIN_COINS} coins), then pay with PayPal.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Purchased coins are for tipping others. If you cash out instead of tipping,
        you’ll forfeit the value you paid.
      </Alert>

      {/* ───── Custom amount ───── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          label="Custom Coins"
          type="number"
          value={customCoins}
          onChange={(e) => setCustomCoins(e.target.value)}
          inputProps={{ min: MIN_COINS }}
        />
        <Tooltip
          title={customInvalid ? `Please enter at least ${MIN_COINS} coins` : ''}
          disableHoverListener={!customInvalid}
        >
          <span>
            <Button
              variant="outlined"
              disabled={buying || customInvalid}
              onClick={() => handleBuyClick({ coins: coinQty, price: customPrice })}
            >
              {buying && selectedPack?.coins === coinQty
                ? <CircularProgress size={20} />
                : (
                  <>
                    Buy {customCoins || 0} for ${customPrice}
                    {customSavings > 0.01 && ` • You save $${customSavings}`}
                  </>
                )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ───── Predefined packs ───── */}
      <Grid container spacing={3}>
        {coinPacks.map((pack) => {
          const savings = Number((pack.coins * COIN_PRICE - pack.price).toFixed(2));
          return (
            <Grid item xs={12} sm={6} md={3} key={pack.coins}>
              <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h6">🪙 {pack.coins} Coins</Typography>
                <Typography variant="subtitle1">${pack.price.toFixed(2)}</Typography>
                <Divider sx={{ my: 1 }} />
                {savings > 0.01 && (
                  <Typography variant="caption" color="textSecondary">
                    You save ${savings}
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  onClick={() => handleBuyClick(pack)}
                  disabled={buying}
                >
                  {buying && selectedPack?.coins === pack.coins
                    ? <CircularProgress size={20} />
                    : 'Buy Now'}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* ───── PayPal dialog ───── */}
      <Dialog open={paypalOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>
          Pay ${selectedPack?.price.toFixed(2)} for {selectedPack?.coins} Coins
        </DialogTitle>
        <DialogContent>
          {selectedPack && (
            <Box position="relative">
              <PayPalButtons
                forceReRender={[selectedPack.price]}
                style={{ layout: 'vertical' }}
                createOrder={(data, actions) => actions.order.create({
                  purchase_units: [{
                    amount: { value: selectedPack.price.toFixed(2) },
                    description: `Buy ${selectedPack.coins} PlanyCoins`,
                  }],
                })}
                onApprove={onApprove}
                onCancel={handlePaypalClose}
                onError={(err) => {
                  console.error('PayPal SDK error', err);
                  alert('PayPal error, please try again.');
                  setPaypalOpen(false);
                }}
                onClick={() => (ppLoading ? Promise.reject() : Promise.resolve())}
              />
              {ppLoading && (
                <Box
                  position="absolute"
                  inset={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="rgba(255,255,255,0.6)"
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !ppLoading && setPaypalOpen(false)} disabled={ppLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───── Success dialog ───── */}
      <Dialog open={successOpen} onClose={handleSuccessClose} maxWidth="xs" fullWidth>
        <DialogTitle>Purchase Successful!</DialogTitle>
        <DialogContent>
          <Typography>
            You’ve successfully purchased {selectedPack?.coins} PlanyCoins.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessClose} variant="contained">
            Go to Wallet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
